import { Link, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";

export function FullScreenLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background" data-testid="app-loading">
      <div className="flex items-center gap-3 text-sm text-muted-foreground">
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        Tamreer
      </div>
    </div>
  );
}

/** Gate for signed-in users (optionally limited to `roles`). Handles the first-login password step and deep-link return. */
export function RequireAuth({ children, roles }) {
  const { user, loading, signedOut } = useAuth();
  const { t } = useLang();
  const location = useLocation();
  if (loading) return <FullScreenLoader />;
  // Remember where the visitor wanted to go (deep link / expired session) so AuthPage can return there after sign-in —
  // but NOT after an explicit sign-out, otherwise the next account to sign in would land on the previous user's page.
  if (!user) return <Navigate to="/auth" state={signedOut?.current ? null : { from: location }} replace />;
  // Invited owners signed in with a one-time password must choose their own before using the platform.
  if (user.must_change_password && location.pathname !== "/set-password") return <Navigate to="/set-password" replace />;
  if (roles && !roles.includes(user.role)) {
    return (
      <div className="rounded-xl border border-border bg-card p-10 text-center" data-testid="access-denied">
        <h2 className="text-lg font-semibold">{t("access_denied")}</h2>
        <Button asChild className="mt-4"><Link to="/dashboard">{t("go_home")}</Link></Button>
      </div>
    );
  }
  return children;
}
